import { useCallback, useEffect, useState } from 'react';

export interface Settings {
  theme: 'system' | 'light' | 'dark';
  palette: 'default' | 'colorblind';
  reduceMotion: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  palette: 'default',
  reduceMotion: false,
};

const KEY = 'champdle.settings';

/** Settings live in this browser only. Storage can be blocked, so every access is guarded. */
export function loadSettings(): Settings {
  try {
    const raw = JSON.parse(window.localStorage.getItem(KEY) ?? 'null') as Partial<Settings> | null;
    return {
      theme: raw?.theme === 'light' || raw?.theme === 'dark' ? raw.theme : 'system',
      palette: raw?.palette === 'colorblind' ? 'colorblind' : 'default',
      reduceMotion: raw?.reduceMotion === true,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(settings: Settings): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    /* private mode or blocked storage: the setting still applies for this visit */
  }
}

export function applySettings(settings: Settings): void {
  const root = document.documentElement;
  const flag = (name: string, value: string | null): void => {
    if (value === null) root.removeAttribute(name);
    else root.setAttribute(name, value);
  };
  flag('data-theme', settings.theme === 'system' ? null : settings.theme);
  flag('data-palette', settings.palette === 'default' ? null : settings.palette);
  flag('data-motion', settings.reduceMotion ? 'reduce' : null);
}

export function useSettings(): [Settings, (patch: Partial<Settings>) => void] {
  const [settings, setSettings] = useState<Settings>(loadSettings);
  useEffect(() => applySettings(settings), [settings]);
  const update = useCallback((patch: Partial<Settings>): void => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);
  return [settings, update];
}
